import { useMemo, useState, useEffect, useRef, type RefObject } from 'react';
import type { EditorProps } from '../types/editor-props';
import {
  InvoiceLinesForm,
  InvoiceLinesValidationError,
  type InvoiceLineFormItem,
  type InvoiceLinesFormData
} from '../../../forms/invoice-lines/InvoiceLinesForm';
import { rebaseInvoiceLineIssues, toCleanInvoiceLines } from '../../../forms/invoice-lines/utils';
import type { ValidationIssue } from '../../../../types';
import { InlineEditorActions } from './InlineEditorActions';
import { useTranslation } from '../../../../providers/UIProvider';
import {
  useInvoiceIsUnitPriceInclVat,
  useInvoiceRecomputeLine,
  useInvoiceVatRateOptions
} from '../../../../providers/InvoiceProvider';
import { toInvoiceLineItems } from '../utils/invoice-lines';

/**
 * Inline editor for invoice line items.
 * Renders InvoiceLinesForm directly in-place (no modal). Intended for the
 * ScanEditor review panel where opening a dialog would be disruptive.
 */
interface InvoiceLinesInlineEditorProps extends EditorProps {
  scrollerRef?: RefObject<HTMLElement | null>;
}

export function InvoiceLinesInlineEditor({
  value,
  onCommit,
  onCancel,
  errors: externalErrors,
  onRegisterSave,
  onDraftChange,
  scrollerRef
}: InvoiceLinesInlineEditorProps) {
  const t = useTranslation('modules.modals.editInvoiceLines');
  const vatRateOptions = useInvoiceVatRateOptions();
  const initialIsUnitPriceInclVat = useInvoiceIsUnitPriceInclVat();
  const onRecomputeLine = useInvoiceRecomputeLine();

  const [lines, setLines] = useState<InvoiceLineFormItem[]>(() =>
    toInvoiceLineItems(value).map(l => ({ ...l }))
  );
  // The editor stays mounted, so a saved value (with the ids of new lines) has to be taken over.
  const isDirtyRef = useRef(false);
  const latestValueRef = useRef(value);
  latestValueRef.current = value;
  useEffect(() => {
    if (!isDirtyRef.current) setLines(toInvoiceLineItems(value).map(l => ({ ...l })));
  }, [value]);
  const [isUnitPriceInclVat, setIsUnitPriceInclVat] = useState(initialIsUnitPriceInclVat ?? false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<ValidationIssue | null>(null);

  const combinedErrors = useMemo<ValidationIssue[]>(
    () =>
      rebaseInvoiceLineIssues(
        saveError ? [...(externalErrors ?? []), saveError] : (externalErrors ?? [])
      ),
    [externalErrors, saveError]
  );

  const handleChange = (data: InvoiceLinesFormData) => {
    isDirtyRef.current = true;
    setLines(data.invoiceLines);
    setIsUnitPriceInclVat(data.isUnitPriceInclVat);
    onDraftChange?.();
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const payload: InvoiceLinesFormData = {
        invoiceLines: toCleanInvoiceLines(lines),
        isUnitPriceInclVat
      };
      if (onCommit) {
        await onCommit(payload);
      }
      // Saved: continue from the server's lines, so every line now carries its id.
      isDirtyRef.current = false;
      setLines(toInvoiceLineItems(latestValueRef.current).map(l => ({ ...l })));
    } catch (error) {
      if (error instanceof InvoiceLinesValidationError) {
        // Surface per-field issues back onto the form via externalErrors propagation.
      } else {
        setSaveError({
          __typename: 'ValidationIssue',
          code: 'SAVE_ERROR',
          groups: [],
          message: error instanceof Error ? error.message : t('errors.saveError'),
          path: '',
          severity: 'error'
        });
      }
      if (onRegisterSave) throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
  };

  // Register handleSave with the ScanEditor global save coordinator.
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  useEffect(() => {
    if (!onRegisterSave) return;
    onRegisterSave(() => handleSaveRef.current());
    return () => onRegisterSave(null);
  }, [onRegisterSave]);

  return (
    <div className='flex flex-col gap-sm w-full' onMouseDown={e => e.stopPropagation()}>
      <InvoiceLinesForm
        value={lines}
        isUnitPriceInclVat={isUnitPriceInclVat}
        onChange={handleChange}
        errors={combinedErrors}
        vatRateOptions={vatRateOptions}
        disabled={saving}
        onRecomputeLine={onRecomputeLine}
        scrollerRef={scrollerRef}
      />
      {!onRegisterSave && (
        <InlineEditorActions t={t} saving={saving} onCancel={handleCancel} onSave={handleSave} />
      )}
    </div>
  );
}

InvoiceLinesInlineEditor.displayName = 'InvoiceLinesInlineEditor';
